import { config } from '../config';
import { isTokenExpired, isTokenExpiringSoon } from './jwtUtils';
import { toast } from 'sonner';

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  // id: string;
  // login: string;
}

class ApiClient {
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  private async refreshToken(): Promise<string> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const newAccessToken = await this.refreshPromise;
      return newAccessToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      console.log('ApiClient: Refreshing token...');
      const response = await fetch(`${config.apiUrl}refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data: RefreshTokenResponse = await response.json();
      
      // Update localStorage with new tokens and preserve user info
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      
      // Update user data with preserved id and login
      // const existingUser = localStorage.getItem('user');
      // if (existingUser) {
      //   const userData = JSON.parse(existingUser);
      //   userData.id = data.id;
      //   userData.login = data.login;
      //   localStorage.setItem('user', JSON.stringify(userData));
      // }
      
      console.log('ApiClient: Token refreshed successfully with user info preserved');
      return data.access_token;
    } catch (error) {
      console.error('ApiClient: Token refresh failed:', error);
      // Clear all tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      
      // Dispatch custom event to notify AuthContext
      window.dispatchEvent(new CustomEvent('tokenRefreshFailed'));
      
      throw error;
    }
  }

  private async getValidAccessToken(): Promise<string> {
    let accessToken = localStorage.getItem('access_token');
    
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Check if token is expired or expiring soon
    if (isTokenExpired(accessToken) || isTokenExpiringSoon(accessToken)) {
      console.log('ApiClient: Access token expired or expiring soon, refreshing...');
      accessToken = await this.refreshToken();
    }

    return accessToken;
  }

  async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const accessToken = await this.getValidAccessToken();
      
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          ...options.headers,
        },
      });

      if (response.status === 401) {
        // Token might be invalid, try to refresh once more
        console.log('ApiClient: Received 401, attempting token refresh...');
        const newAccessToken = await this.refreshToken();
        
        // Retry the request with new token
        const retryResponse = await fetch(`${config.apiUrl}${endpoint}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${newAccessToken}`,
            ...options.headers,
          },
        });

        if (!retryResponse.ok) {
          throw new Error(`Request failed: ${retryResponse.statusText}`);
        }

        return retryResponse.json();
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('ApiClient: API request failed:', error);
      throw error;
    }
  }

  // Convenience methods
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
